import React, { useState, useEffect, useRef } from 'react';

import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { throttle } from 'lodash';

import AuthState from '@redux/auth/auth-state.interface';

import HeaderMenu, { MenuItemEntry, MenuActions } from './menu/menu';
import { MENU_LINKS } from '@config/menu';
import { MENU_ACTIONS } from './menu/menu-actions';

import LoginModal from './modal/login-modal';
import SignupModal from './modal/signup-modal';
import SignupSuccessModal from './modal/signup-success-modal';
import ConfirmLogoutModal from './modal/confirm-logout-modal';
import ForgotPasswordModal from './modal/forgot-password-modal';
import VerifyPartnerSuccessModal from './modal/verify-partner-success-modal';

import boonLogo from '@assets/images/boon-logo.png';

const HEADER_OFFSET = 40;

export type HeaderMode = 'sticky' | 'fixed' | 'fixed-sticky';

export interface HeaderProps {
    mode?: HeaderMode;
    baseHeightClass?: string;
    baseContentWidthClass?: string;
    navBaseHeightClass?: string;
    navBaseContentWidthClass?: string;
    logoLink?: string;
    isLogoAbsolute?: boolean;
    menuLinks?: MenuItemEntry[];
    menuActions?: MenuActions;
    translationCallback?: typeof useTranslation;
}

const Header: React.FC<HeaderProps> = props => {
    const ref = useRef();
    const user = useSelector((state: { auth: AuthState }) => state.auth.user);
    const [mode, setMode] = useState(props.mode === 'fixed-sticky' ? 'fixed' : props.mode);

    let headerClasses;

    switch (mode) {
        case 'sticky':
            headerClasses = 'fixed bg-white shadow-lg opacity-95';
            break;
        case 'fixed':
            headerClasses = 'absolute bg-transparent mt-4 lg:mt-6';
            break;
    }

    const handleScroll = () => {
        if (props.mode === 'fixed-sticky') {
            if (window.pageYOffset > HEADER_OFFSET) {
                setMode('sticky');
            } else {
                setMode('fixed');
            }
        }
    };

    const generateLogo = (href = '#') => (
        <a href={href} className="h-full inline-flex flex-col items-center justify-center">
            <img
                src={boonLogo}
                className={'Logo object-contain ' + (mode === 'fixed' ? 'h-10 md:h-11 xl:h-17' : 'h-8 md:h-10 xl:h-12')}
            />
        </a>
    );

    useEffect(() => {
        window.addEventListener('scroll', throttle(handleScroll, 100), { passive: true });

        return () => {
            window.removeEventListener('scroll', throttle(handleScroll, 100));
        };
    }, []);

    return (
        <header
            ref={ref}
            className={
                `mode-${mode} ` +
                'w-screen base-header-height flex justify-center items-center top-0 z-10 ' +
                headerClasses
            }
        >
            <style jsx>{`
                :global(header.mode-sticky) {
                    transition: all 0.3s linear;
                    transition: background-color 0.5s linear;
                }

                :global(header .Logo) {
                    transition: all 0.3s linear;
                }

                :global(main) {
                    padding-top: ${props.mode === 'fixed' || props.mode === 'fixed-sticky' ? '0 !important;' : ''};
                }
            `}</style>

            <nav
                className={
                    'flex flex-wrap items-center justify-between ' +
                    props.navBaseContentWidthClass +
                    ' ' +
                    props.navBaseHeightClass
                }
            >
                <div className="Logo flex-1 h-full">
                    {props.isLogoAbsolute ? (
                        generateLogo(props.logoLink)
                    ) : (
                        <Link href={props.logoLink}>{generateLogo()}</Link>
                    )}
                </div>

                <div className="MenuWrapper h-full flex items-center">
                    <HeaderMenu
                        backgroundClass={mode === 'fixed' ? 'bg-white-80' : 'bg-white-95'}
                        subMenuBackgroundClass={mode === 'fixed' ? 'lg:bg-white-80' : 'lg:bg-white-95'}
                        menuActions={props.menuActions}
                        menuLinks={props.menuLinks}
                        translationCallback={props.translationCallback}
                    />
                </div>
            </nav>

            {!user ? (
                <React.Fragment>
                    <LoginModal />
                    <SignupModal />
                    <SignupSuccessModal />
                    <ForgotPasswordModal />
                    <VerifyPartnerSuccessModal />
                </React.Fragment>
            ) : (
                <ConfirmLogoutModal />
            )}
        </header>
    );
};

Header.defaultProps = {
    mode: 'sticky',
    logoLink: '/',
    isLogoAbsolute: false,
    baseHeightClass: 'base-header-height',
    baseContentWidthClass: 'base-content-width-wide',
    navBaseHeightClass: 'h-full',
    navBaseContentWidthClass: 'base-content-width-wide',
    menuLinks: MENU_LINKS,
    menuActions: MENU_ACTIONS,
};

export default Header;
